import api from './api';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface User {
  id: string;
  first_name: string;
  family_name: string;
  email: string;
  username: string;
  role: string;
  user_type: string;
  status: string;
  client_id?: string;
  is_active: boolean;
}

export interface MembershipInfo {
  membership_id: string;
  client_id: string;
  client_name: string;
  role: string;
}

export interface LoginResponse {
  success: boolean;
  user: User | null;
  permissions: Record<string, unknown>;
  message: string;
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
  // Multi-tenant: list of clients user can access
  memberships?: MembershipInfo[];
  requires_client_selection?: boolean;
}

export interface SelectClientResponse {
  success: boolean;
  message: string;
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  selected_client_id: string;
  role: string;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', {
      username: credentials.username,
      password: credentials.password,
    });

    if (response.data.success && response.data.user) {
      sessionStorage.setItem('currentUser', JSON.stringify(response.data.user));
      sessionStorage.setItem('userPermissions', JSON.stringify(response.data.permissions));

      if (response.data.access_token) {
        sessionStorage.setItem('access_token', response.data.access_token);
      }
      if (response.data.refresh_token) {
        sessionStorage.setItem('refresh_token', response.data.refresh_token);
      }
      
      // Store memberships if available
      if (response.data.memberships) {
        sessionStorage.setItem('userMemberships', JSON.stringify(response.data.memberships));
      }
    }

    return response.data;
  },

  async selectClient(clientId: string): Promise<SelectClientResponse> {
    const response = await api.post<SelectClientResponse>('/auth/select-client', {
      client_id: clientId,
    });

    if (response.data.success) {
      // Update tokens with new client context
      sessionStorage.setItem('access_token', response.data.access_token);
      sessionStorage.setItem('refresh_token', response.data.refresh_token);
      sessionStorage.setItem('selectedClientId', response.data.selected_client_id);
      
      // Update user's client_id in stored user
      const currentUser = this.getCurrentUser();
      if (currentUser) {
        currentUser.client_id = response.data.selected_client_id;
        currentUser.role = response.data.role;
        sessionStorage.setItem('currentUser', JSON.stringify(currentUser));
      }
    }

    return response.data;
  },

  async logout(): Promise<void> {
    sessionStorage.removeItem('currentUser');
    sessionStorage.removeItem('userPermissions');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('userMemberships');
    sessionStorage.removeItem('selectedClientId');
  },

  isAuthenticated(): boolean {
    // Check both user and token exist
    const hasUser = !!sessionStorage.getItem('currentUser');
    const hasToken = !!sessionStorage.getItem('access_token');
    return hasUser && hasToken;
  },

  getCurrentUser(): User | null {
    const userStr = sessionStorage.getItem('currentUser');
    return userStr ? JSON.parse(userStr) : null;
  },

  getPermissions(): Record<string, unknown> | null {
    const permsStr = sessionStorage.getItem('userPermissions');
    return permsStr ? JSON.parse(permsStr) : null;
  },

  getAccessToken(): string | null {
    return sessionStorage.getItem('access_token');
  },

  getRefreshToken(): string | null {
    return sessionStorage.getItem('refresh_token');
  },

  getMemberships(): MembershipInfo[] {
    const membershipsStr = sessionStorage.getItem('userMemberships');
    return membershipsStr ? JSON.parse(membershipsStr) : [];
  },

  getSelectedClientId(): string | null {
    return sessionStorage.getItem('selectedClientId');
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },
};
